import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../auth";
import { toast } from "sonner";
import { Send, X, LifeBuoy, Loader2, Sparkles } from "lucide-react";

const AVATAR = "/arjun-avatar.jpeg";

const AUTH_PROMPTS = [
  "How do I add a member?",
  "How do I delete a member?",
  "How does the Excel bulk upload work?",
  "How many endorsements do I have pending?",
];
const PUBLIC_PROMPTS = [
  "What is InsureHub?",
  "What is a group insurance endorsement?",
  "What does the QCR benchmarking do?",
  "How do I get started?",
];

function sessionId(key) {
  let s = sessionStorage.getItem(key);
  if (!s) { s = `arjun_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(key, s); }
  return s;
}

export default function ArjunChat({ user, publicMode = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const sid = useRef(sessionId(publicMode ? "arjun_session_public" : "arjun_session"));
  const scrollRef = useRef(null);
  const firstName = publicMode ? "" : ((user?.full_name || user?.name || user?.username || "there").split(" ")[0]);
  const prompts = publicMode ? PUBLIC_PROMPTS : AUTH_PROMPTS;

  const greeting = publicMode
    ? "Hi! I'm Arjun, the InsureHub assistant. Ask me what InsureHub does, about group-insurance basics, or how to get started. 👋"
    : `Hi ${firstName}! I'm Arjun, your InsureHub assistant. Ask me how to submit endorsements, set up rate cards, run benchmarks, or any group-insurance question. If I can't help, you can raise it to your admin.`;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending, streaming]);

  const sendText = async (text) => {
    text = (text || "").trim();
    if (!text || sending || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);

    const url = publicMode ? `${API}/assistant/public-chat/stream` : `${API}/assistant/chat/stream`;
    const headers = { "Content-Type": "application/json", ...(publicMode ? {} : getAuthHeaders()) };

    try {
      const resp = await fetch(url, { method: "POST", headers, body: JSON.stringify({ session_id: sid.current, message: text }) });
      if (!resp.ok || !resp.body) throw new Error("stream failed");

      // First token flips us from "typing…" to a live, growing bubble
      let started = false;
      let errored = false;
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const appendDelta = (delta) => {
        if (!started) {
          started = true;
          setSending(false);
          setStreaming(true);
          setMessages((m) => [...m, { role: "assistant", content: delta }]);
        } else {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
            return copy;
          });
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let data;
          try { data = JSON.parse(line); } catch { continue; }
          if (data.delta) appendDelta(data.delta);
          else if (data.error) errored = true;
        }
      }

      if (errored && !started) throw new Error("model error");
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: publicMode
        ? "Sorry, I'm having trouble right now. Please try again in a moment."
        : "Sorry, I'm having trouble right now. You can tap 'Raise to admin' and I'll pass it on." }]);
    } finally {
      setSending(false);
      setStreaming(false);
    }
  };

  const send = () => sendText(input);

  const escalate = async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    const q = (lastUser?.content || input).trim();
    if (!q) { toast.error("Type your question first, then raise it to admin"); return; }
    setEscalating(true);
    try {
      const res = await axios.post(`${API}/assistant/escalate`, { session_id: sid.current, query: q }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setMessages((m) => [...m, { role: "assistant", content: "I've raised this to your admin — they'll get back to you soon." }]);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't raise the query"); }
    finally { setEscalating(false); }
  };

  const showPrompts = messages.length <= 1 && !sending && !streaming;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-[60] flex items-center gap-2 rounded-full bg-white shadow-xl shadow-black/10 border border-stone-200 pl-1.5 pr-4 py-1.5 hover:shadow-2xl transition-shadow"
          data-testid="arjun-open-btn"
        >
          <img src={AVATAR} alt="Arjun" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#3DA635]/40" />
          <span className="text-sm font-semibold text-stone-700">Ask Arjun</span>
          <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#3DA635] ring-2 ring-white" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl bg-white shadow-2xl border border-stone-200 overflow-hidden" data-testid="arjun-panel">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#3DA635] to-[#2E73B8] text-white">
            <img src={AVATAR} alt="Arjun" className="h-10 w-10 rounded-full object-cover ring-2 ring-white/60" />
            <div className="leading-tight flex-1 min-w-0">
              <p className="font-bold text-sm">Arjun</p>
              <p className="text-[11px] text-white/85">InsureHub Assistant · online</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white/20" data-testid="arjun-close-btn"><X className="w-4 h-4" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#FBFAF7]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <img src={AVATAR} alt="" className="h-6 w-6 rounded-full object-cover mr-2 mt-0.5 shrink-0" />}
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#2E73B8] text-white rounded-br-sm" : "bg-white border border-stone-200 text-stone-700 rounded-bl-sm"}`}>
                  {m.content}
                  {streaming && i === messages.length - 1 && m.role === "assistant" && (
                    <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-[#3DA635] animate-pulse" />
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start items-center gap-2 text-stone-400 text-xs"><img src={AVATAR} alt="" className="h-6 w-6 rounded-full object-cover" /><Loader2 className="w-3.5 h-3.5 animate-spin" /> Arjun is typing…</div>
            )}

            {/* Suggested starter prompts */}
            {showPrompts && (
              <div className="pt-1 space-y-2" data-testid="arjun-suggested-prompts">
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-stone-400 uppercase tracking-wide">
                  <Sparkles className="w-3 h-3" /> Try asking
                </div>
                <div className="flex flex-wrap gap-2">
                  {prompts.map((p, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendText(p)}
                      className="text-xs text-left px-3 py-1.5 rounded-full bg-white border border-[#3DA635]/40 text-stone-600 hover:bg-[#3DA635]/10 hover:border-[#3DA635] transition-colors"
                      data-testid={`arjun-prompt-${idx}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {!publicMode && (
            <div className="px-3 pt-2">
              <button onClick={escalate} disabled={escalating} className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-[#E05A47] hover:text-[#C94837] py-1.5 rounded-lg border border-dashed border-[#E05A47]/40 hover:bg-[#E05A47]/5 transition-colors disabled:opacity-60" data-testid="arjun-escalate-btn">
                {escalating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LifeBuoy className="w-3.5 h-3.5" />} Not resolved? Raise to admin
              </button>
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask Arjun anything…"
              className="flex-1 rounded-full border border-stone-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3DA635]/30"
              data-testid="arjun-input"
            />
            <button onClick={send} disabled={sending || streaming || !input.trim()} className="h-9 w-9 rounded-full bg-[#3DA635] text-white flex items-center justify-center hover:bg-[#2f8a2a] disabled:opacity-50" data-testid="arjun-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
